import type { Invoice } from "@freightsimple/generated-dashboard-openapi-client/models/Invoice";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { now } from "@freightsimple/time/helpers";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { FileZipIcon } from "@phosphor-icons/react/FileZip";
import type { ReactNode } from "react";
import { useState } from "react";
import { useShipmentService } from "@/Services/ShipmentService";
import { PaymentsDownloadInvoicesExcelButton } from "../Buttons/PaymentsDownloadInvoicesExcelButton";
import { PaymentsPaidInvoicesTable } from "../Tables/PaymentsPaidInvoicesTable";
import { PaymentsStatusType } from "../Types/PaymentsStatusType";

interface PaymentsMakePaymentsReceiptDialogProps {
  open: boolean;
  /** Closing the receipt ends the whole flow — the owner closes both dialogs. */
  onOpenChange: (open: boolean) => void;
  /** Completed (card, charged now) or Pending (bank/manual, settling). */
  paymentStatus: PaymentsStatusType;
  totalAmount: number;
  currency: string;
  selectedPaymentMethod: ReactNode;
  companyName: string;
  invoices: Invoice[];
  shipmentMap: Map<string, Shipment>;
}

function ReceiptDetail_({
  label,
  testId,
  children,
}: {
  label: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Stack gap={1} align="center" data-testid={testId}>
      <T size="xs" weight="medium" color="muted">
        {label}
      </T>
      <T weight="medium" color="secondary" align="center">
        {children}
      </T>
    </Stack>
  );
}

/**
 * The receipt after a payment goes through: the total, how it was paid, and
 * every invoice it covered, with both download actions in the header.
 */
export function PaymentsMakePaymentsReceiptDialog({
  open,
  onOpenChange,
  paymentStatus,
  totalAmount,
  currency,
  selectedPaymentMethod,
  companyName,
  invoices,
  shipmentMap,
}: PaymentsMakePaymentsReceiptDialogProps) {
  const shipmentService = useShipmentService();
  const [isDownloading, setIsDownloading] = useState(false);

  const completed = paymentStatus === PaymentsStatusType.Completed;

  // Today's date in UTC, formatted MM/DD/YYYY. now() is a UTC ISO timestamp,
  // so slicing the date part keeps the original UTC-based behaviour.
  const [year, month, day] = now().slice(0, 10).split("-");
  const currentDate = `${month}/${day}/${year}`;

  const rows = invoices.flatMap((invoice) => {
    const shipment = shipmentMap.get(invoice.shipmentId);
    if (!shipment) {
      console.error("Shipment not found for", invoice.invoiceId);
      return [];
    }
    return [{ invoice, shipment }];
  });

  const handleDownloadAll = async () => {
    const firstInvoice = invoices[0];
    if (firstInvoice === undefined) {
      return;
    }
    try {
      setIsDownloading(true);
      await shipmentService.downloadInvoicesZip(
        firstInvoice.shipmentId,
        invoices.map((invoice) => ({
          invoicePdfKey: invoice.invoicePdfKey,
          invoiceIdentifier: invoice.invoiceIdentifier,
        })),
        `invoices__${currentDate}.zip`,
      );
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="wide"
      title={completed ? "Payment Completed" : "Payment Processing"}
      description={
        completed
          ? "Your payment has been completed successfully!"
          : "Thank you! We are processing your payment. This may take up to 3 business days."
      }
      footer={
        <Row grow justify="center">
          <Button primary onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </Row>
      }
    >
      {/* 10, not 6, before the invoice list: the total + details read as one
        summary block, and the table needs a clearer break from it. */}
      <Stack gap={10} align="stretch">
        <Stack gap={6} align="stretch">
          <Stack align="center" gap={1}>
            <Icon
              icon={CheckCircleIcon}
              weight="fill"
              size="3xl"
              color={completed ? "text-green" : "text-light-blue"}
            />
            <T size="lg" weight="medium" color="muted">
              TOTAL:
            </T>
            <Row gap={1}>
              <T size="3xl" weight="medium" color="secondary" nowrap>
                ${totalAmount}
              </T>
              <T size="3xl" weight="medium" color="muted" nowrap>
                {currency}
              </T>
            </Row>
          </Stack>

          <Row gap={6} align="start" justify="center" wrap>
            <ReceiptDetail_
              label="Payment Status"
              testId="receipt-payment-status"
            >
              {completed ? "Completed" : "Pending"}
            </ReceiptDetail_>
            <ReceiptDetail_ label="Date" testId="receipt-date">
              {currentDate}
            </ReceiptDetail_>
            <ReceiptDetail_
              label="Payment Method"
              testId="receipt-payment-method"
            >
              {selectedPaymentMethod}
            </ReceiptDetail_>
            <ReceiptDetail_ label="Company" testId="receipt-company">
              {companyName}
            </ReceiptDetail_>
          </Row>
        </Stack>

        <Stack gap={3} align="stretch">
          {/* Labeled buttons here, not in headerActions: the dialog-header
              slot renders Buttons as compact icon-actions by design, and
              these two need their labels to be told apart. */}
          <Row justify="between" align="center">
            <T weight="medium">Paid Invoices ({invoices.length})</T>
            <Row gap={2}>
              <PaymentsDownloadInvoicesExcelButton
                invoices={invoices}
                shipmentMap={shipmentMap}
              />
              <Button
                loading={isDownloading}
                onClick={handleDownloadAll}
                icon={FileZipIcon}
              >
                Download PDFs
              </Button>
            </Row>
          </Row>
          <PaymentsPaidInvoicesTable rows={rows} />
        </Stack>
      </Stack>
    </Dialog>
  );
}
